import React, { useState, useRef, useEffect } from "react";
import closeIcon from '../images/close.svg';
import minimizeIcon from '../images/minimize.svg';
import maximizeIcon from '../images/maximize.svg';
import normalizeIcon from '../images/normalize.svg';
import openIcon from '../images/open.svg';

const Window = ({ title, onClose, children, bringToFront, zIndex, defaultSize, isMinimized, onMinimize, onRestore, minimizedPosition }) => {  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [size, setSize] = useState(defaultSize);
  const [isInteracting, setIsInteracting] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const windowRef = useRef(null);
  const dragStartPos = useRef(null);
  const isResizing = useRef(false);
  const isDragging = useRef(false);
  const originalSize = useRef(defaultSize);
  const originalPosition = useRef({ x: 100, y: 100 });
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 700);

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  // Update position if minimizedPosition changes
  useEffect(() => {
    if (isMinimized && minimizedPosition) {
      // Save original size and position before minimizing
      originalSize.current = size;
      originalPosition.current = position;
      setPosition(minimizedPosition);
      setSize({ width: minimizedPosition.width, height: 30 });
    }
    else if (isSmallScreen) {
      setSize({ width: window.innerWidth, height: window.innerHeight });
      setPosition({ x: 0, y: 0 });
      setIsMaximized(true);
    }
  }, [minimizedPosition, isMinimized]);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 700);
      console.log(window.innerWidth)
      console.log(isMaximized);
    };
  
    window.addEventListener('resize', handleResize);
  
    // Cleanup the event listener when the component unmounts
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle mouse move when dragging or resizing
  const handlePointerMove = (e) => {
    if (isDragging.current) {
      const dx = e.clientX - dragStartPos.current.x;
      const dy = e.clientY - dragStartPos.current.y;
      const newX = dragStartPos.current.startX + dx;
      const newY = dragStartPos.current.startY + dy;

      setPosition({
        x: clamp(newX, 0, window.innerWidth - size.width),
        y: clamp(newY, 0, window.innerHeight - 30),
      });
    }

    if (isResizing.current) {
      setSize({
        width: Math.max(200, e.clientX - position.x),
        height: Math.max(150, e.clientY - position.y),
      });
    }
  };

  // Stop dragging or resizing
  const handlePointerUp = () => {
    isDragging.current = false;
    isResizing.current = false;
    setIsInteracting(false);
    document.removeEventListener("pointermove", handlePointerMove);
    document.removeEventListener("pointerup", handlePointerUp);
  };

  // Start dragging window
  const handlePointerDown = (e) => {
    if (isMinimized) {
      e.stopPropagation();
      // Restore to default size (not originalSize which might be maximized)
      setSize(defaultSize);
      setPosition({ x: 100, y: 100 }); // Or any default position
      setIsMaximized(false); // Reset maximized state
      onRestore();
      return;
    }
    
    bringToFront();
    isDragging.current = true;
    dragStartPos.current = { 
      x: e.clientX, 
      y: e.clientY, 
      startX: position.x, 
      startY: position.y 
    };
    setIsInteracting(true);
    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", handlePointerUp);
  };

  // Start resizing window
  const handleResizePointerDown = (e) => {
    e.stopPropagation();
    isResizing.current = true;
    setIsInteracting(true);
    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", handlePointerUp);
  };

  // Toggle maximize/restore window
  const toggleMaximize = (e) => {
    e.stopPropagation();
    if (isMaximized) {
      setSize(defaultSize); // Use defaultSize instead of originalSize
      setPosition({ x: 100, y: 100 }); // Or your preferred default position
    } else {
      // Only store current size/position if window isn't minimized
      if (!isMinimized) {
        originalSize.current = size;
        originalPosition.current = position;
      }
      setSize({ width: window.innerWidth, height: window.innerHeight });
      setPosition({ x: 0, y: 0 });
    }
    setIsMaximized(!isMaximized);
    bringToFront();
  };

  return (
    <div
      ref={windowRef}
      className="absolute border-4 border-gray-600 bg-white shadow-lg overflow-hidden flex flex-col"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
        zIndex: isMinimized ? zIndex : zIndex + 100,
        pointerEvents: isInteracting ? 'none' : 'auto',
      }}
    >
      {/* Window Header */}
      <div
        className="bg-gray-600 text-white text-sm flex justify-between items-center h-6"
        onPointerDown={handlePointerDown}
        style={{ padding: "0px 4px", pointerEvents: 'all' }}
      >
        <span className="flex-1 truncate">{title}</span>
        <div className="flex space-x-2 items-center">

          {!isMinimized && (
            <button 
              onClick={(e) => { e.stopPropagation(); onMinimize();}} 
              className="w-[13px] h-[13px] rounded-full flex items-center justify-center group"
            >
              <img src={minimizeIcon} alt="Minimize"/>
            </button>
          )}
          {!isMinimized && (
            <button 
              onClick={toggleMaximize} 
              className="w-[13px] h-[13px] rounded-full flex items-center justify-center group"
            >
              {isMaximized ? <img src={normalizeIcon} alt="Normalize"/> : <img src={maximizeIcon} alt="Maximize"/>}
            </button>
          )}
          {!isMinimized && (
            <button 
              onClick={(e) => { e.stopPropagation(); onClose();}} 
              className="w-[13px] h-[13px] rounded-full flex items-center justify-center group"
            >
              <img src={closeIcon} alt="X"/>
            </button>
          )}
          {isMinimized && (
            <button className="w-[13px] h-[13px] rounded-full flex items-center justify-center group">
              <img src={openIcon} alt="Open"/>
            </button>
          )}
        </div>
      </div>

      {/* Window Content */}
      {!isMinimized && (
        <div className="flex-1 overflow-y-auto bg-gray-300 relative" style={{ height: `calc(100% - 30px)` }}>
          {children}
        </div>
      )}

      {/* Resize Handle */}
      {!isMaximized && !isMinimized && (
        <div
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize bg-gray-600"
          onPointerDown={handleResizePointerDown}
          style={{ pointerEvents: 'all' }}
        />
      )}
    </div>
  );
};

const WindowManager = ({ openWindows, setOpenWindows, size = 2 }) => {
  const [zIndexes, setZIndexes] = useState([]);
  const nextZIndex = useRef(1);

  const closeWindow = (id) => {
    setOpenWindows((prev) => prev.filter((win) => win.id !== id));
  };

  const bringToFront = (index) => {
    nextZIndex.current += 1;
    setZIndexes((prevZIndexes) => {
      const newZIndexes = [...prevZIndexes];
      newZIndexes[index] = nextZIndex.current;
      return newZIndexes;
    });
  };

  const sizeOptions = {
    1: { width: 300, height: 250 },
    2: { width: 500, height: 400 },
    3: { width: 700, height: 550 },
  };

  const minimizeWindow = (index) => {
    setOpenWindows((prev) => {
      const newWindows = [...prev];
      newWindows[index].isMinimized = true;
      return newWindows;
    });
  };

  const restoreWindow = (index) => {
    setOpenWindows((prev) => {
      const newWindows = [...prev];
      newWindows[index].isMinimized = false;
      return newWindows;
    });
    bringToFront(index);
  };

  const calculateMinimizedPositions = () => {
    const minimizedWindows = openWindows.filter(win => win.isMinimized);
    const numMinimized = minimizedWindows.length;
    
    if (numMinimized === 0) return [];
    
    const maxWidth = 300; // Strict max width
    const windowWidth = Math.min(maxWidth, Math.floor(window.innerWidth / numMinimized));
    
    return minimizedWindows.map((win, index) => ({
      x: index * windowWidth,
      y: window.innerHeight - 30,
      width: windowWidth
    }));
  };

  const minimizedPositions = calculateMinimizedPositions();

  // Assign minimized positions to windows
  const windowsWithPositions = openWindows.map((win, index) => {
    if (win.isMinimized) {
      const minimizedIndex = openWindows
        .slice(0, index)
        .filter(w => w.isMinimized).length;
      return {
        ...win,
        minimizedPosition: minimizedPositions[minimizedIndex]
      };
    }
    return win;
  });

  return (
    <div>
      {windowsWithPositions.map((win, index) => (
        <Window
          key={win.id}
          title={win.title}
          onClose={() => closeWindow(win.id)}
          bringToFront={() => bringToFront(index)}
          zIndex={zIndexes[index] || 1}
          defaultSize={sizeOptions[size]}
          isMinimized={win.isMinimized}
          onMinimize={() => minimizeWindow(index)}
          onRestore={() => restoreWindow(index)}
          minimizedPosition={win.minimizedPosition}
        >
          {win.content}
        </Window>
      ))}
    </div>
  );
};

export default WindowManager;